import { useMemo } from 'react';
import { htcmdService } from 'js/services/htcmd.service';

export const useHTCMDPublicAPI = () => {
    const {
        isEnabled,
        isWrapperEnabled,
        sendMessage,
        portalEvent,
        download,
        processBiometricAuthentication,
        login,
        launchgame
    } = htcmdService;

    return useMemo(() => ({
        /**
         * Is `true` if Native integration is enabled.
         * @memberOf Playtech.API.htcmd
         * @type {boolean}
         * @example
         * // false
         * Playtech.API.htcmd.isEnabled;
         */
        isEnabled,
        /**
         * Is `true` if Wrapper integration is enabled.
         * @memberOf Playtech.API.htcmd
         * @type {boolean}
         *  @example
         * // false
         * Playtech.API.htcmd.isWrapperEnabled;
         */
        isWrapperEnabled,
        /**
         * Send command to Native application.
         * @memberOf Playtech.API.htcmd
         * @function
         * @param {string} command Command name.
         * @param {Object} options Plain object with parameters for the command.
         * @param {string} options.event
         * @param {string} options.amount
         * @param {string} options.currency
         * @example
         * Playtech.API.htcmd.sendMessage('portalEvent', { event: 'depositCompleted', amount: '20', currency: 'USD' });
         */
        sendMessage,
        /**
         * Send portalEvent command to Native application.
         * @memberOf Playtech.API.htcmd
         * @function
         * @param {Object} options Plain object with parameters for the command.
         * @param {string} options.event
         * @param {string} options.amount
         * @param {string} options.currency
         * @example
         * Playtech.API.htcmd.portalEvent({ event: 'depositCompleted', amount: '20', currency: 'USD' });
         */
        portalEvent,
        /**
         * Send "authentication/processBiometricAuthentication" command to Native application that Biometric Login is available.
         * @memberOf Playtech.API.htcmd
         * @function
         * @example
         * Playtech.API.htcmd.processBiometricAuthentication();
         */
        processBiometricAuthentication,
        /**
         * Send 'loginsuccess' (?isNative=1) / 'login' (?htcmd=1) to Native application with authentication data.
         * @memberOf Playtech.API.htcmd
         * @function
         * @param {Object} options Plain object with parameters for the command.
         * @example
         * Playtech.API.htcmd.login({ ...options });
         */
        login,
        /**
         * Send 'download' to Native / Wrapper application to initiate file download.
         * @memberOf Playtech.API.htcmd
         * @function
         * @param url {String} Blob URL to download.
         * @param name {String} Name of the file.
         * @param mime {String} MIME type of the file.
         * @example
         * Playtech.API.htcmd.download(url, name, mime);
         */
        download,
        /**
         * Send 'launchgame' to Native application to launch game.
         * @memberOf Playtech.API.htcmd
         * @function
         * @param {Object} options Plain object with parameters for the command.
         * @param {string} options.code Game code.
         * @param {number} options.mode Game mode.
         * @example
         * Playtech.API.htcmd.launchgame({ code: 'gameCode', mode: 1 });
         */
        launchgame
    }), [
        isEnabled,
        isWrapperEnabled,
        download,
        launchgame,
        sendMessage,
        portalEvent,
        processBiometricAuthentication,
        login
    ]);
};
